import { handleError } from "@/api/handleError";
import { useContact } from "@/composables/useContact";
import { useContactStore } from "@/stores/contact.store";
import { useTeamStore } from "@/stores/team-member.store";
import { notify } from "@kyvg/vue3-notification";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import emitter from "./integrations/emitter";
import {
  distributeBulkUploadContacts,
  distributeContacts,
} from "./queries/callQueue";
import { ContactDistribution } from "./types/callQueue";
import { getInitials } from "./utils";
import { useBusinessStore } from "@/stores";
import { retrieveContact } from "@/api/requests/contacts";

const loading = ref(false);

const callQueueLoading = ref(false);

const allContactsSelected = ref(false);

const selectedTeamMembers = ref<number[]>([]);

const selectedTeamUserQueue = ref();

const contactShare = ref<number[]>([]);

const showAddContactsModal = ref(false);

const bulkUploadLoading = ref(false);

const uploadProgressPercentage = ref(0);

const showContactUploadProgressmodal = ref(false);

const importCSVdialogVisible = ref(false);

export const useContactCallQueue = () => {
  const contactStore = useContactStore();

  const businessStore = useBusinessStore();

  const teamStore = useTeamStore();

  const { selectedContacts, selectedContactIDs } = storeToRefs(contactStore);

  const { teamMembers } = storeToRefs(teamStore);

  const { singleMatchAction, multipleMatchResolutionStrategy } = useContact();

  const fullInitials = (name: string) => {
    const fullInitials = getInitials(name);

    return fullInitials ? fullInitials : "";
  };

  const singleteamMember = computed(() => {
    // This takes care of generating the name for a single team member
    return teamMembers.value.find(
      (teamMember) => teamMember.user == selectedTeamMembers.value[0]
    );
  });

  const totalContactsDistributed = computed(() => {
    return contactShare.value.reduce(
      (a: number, b: number) => Number(a) + Number(b),
      0
    );
  });

  const totalContactsSelected = computed(() => {
    return allContactsSelected.value
      ? contactStore.totalContacts
      : selectedContacts.value.length;
  });

  const distributeContactToQueue = async () => {
    const distribution = [];

    loading.value = true;

    for (let i = 0; i < selectedTeamMembers.value.length; i++) {
      distribution.push({
        agent_id: selectedTeamMembers.value[i],
        contact_count: contactShare.value[i],
      });
    }

    if (selectedTeamMembers.value.length == 1) {
      distribution[0].contact_count = totalContactsSelected.value;
    }

    let contacts;
    let payload;
    let response;

    // conditional to check if its an uploaded contact from the first element
    console.log(importContactMode.value);
    if (importContactMode.value) {
      contacts = selectedContacts.value;

      payload = {
        bulk_contact_data: { contacts },
        distribution,
        single_match_action: singleMatchAction.value,
        multiple_match_resolution_strategy:
          multipleMatchResolutionStrategy.value,
      } as ContactDistribution;

      importContactsWithProgress(handleDistributeBulkUploadContact, payload);
      // handleDistributeBulkUploadContact(payload)
    } else {
      contacts = allContactsSelected.value ? null : selectedContactIDs.value;

      payload = {
        contacts,
        distribution,
        all_contacts: allContactsSelected.value,
      } as ContactDistribution;

      response = await handleDistributeContactAPICall(payload);
    }

    loading.value = false;

    if (response == `success`) return "success" as string;
  };

  const handleDistributeContactAPICall = async (
    payload: ContactDistribution
  ) => {
    try {
      await distributeContacts(payload);

      selectedContacts.value = [];

      return "success";
    } catch (error) {
      handleError({
        error,
        fallbackMessage: "Error occured while adding contacts to call queue",
      });
    }
  };

  const handleDistributeBulkUploadContact = async (
    payload: ContactDistribution
  ) => {
    try {
      bulkUploadLoading.value = true;

      await distributeBulkUploadContacts(payload);

      selectedContacts.value = [];

      bulkUploadLoading.value = false;

      return "success";
    } catch (error) {
      handleError({
        error,
        fallbackMessage: "Error occurred while adding contacts to call queue",
      });

      bulkUploadLoading.value = false;
      throw error;
    }
  };

  const importContactMode = computed(() => {
    return selectedContacts.value.length > 0 && !selectedContacts.value[0]?.id;
  });

  const importContactsWithProgress = (
    callback: (payload: ContactDistribution) => Promise<string>,
    payload: ContactDistribution
  ) => {
    showContactUploadProgressmodal.value = true;

    const handleProgressComplete = async () => {
      try {
        // Execute the callback function
        uploadProgressPercentage.value = 80;

        await callback(payload);

        // Set value to 100 after a successful callback execution
        uploadProgressPercentage.value = 100;

        showAddContactsModal.value = false;
        notify({
          type: "success",
          text: "Contacts added to call queue successfully",
        });

        setTimeout(() => {
          showContactUploadProgressmodal.value = false;

          uploadProgressPercentage.value = 0;

          emitter.emit("closeimportCSVModal");
        }, 2000);
      } catch (error) {
        showContactUploadProgressmodal.value = false;

        uploadProgressPercentage.value = 0;

        handleError({ error });
        throw error;
      }
    };

    const intervalId = setInterval(() => {
      uploadProgressPercentage.value++; // Increment the value

      if (uploadProgressPercentage.value > 50) {
        clearInterval(intervalId); // Clear the interval when the value exceeds 50
        void handleProgressComplete();
      }
    }, 100); // 2-second delay
  };

  const handleFetchContact = async (contactId: string) => {
    try {
      if (businessStore.currentBusinessId) {
        const businessId = businessStore.currentBusinessId;
        const response = await retrieveContact({
          contactId,
          businessId,
        });
        return response.data;
      }
      return null;
    } catch (error) {
      handleError({ error });
    }
  };

  return {
    fullInitials,
    loading,
    callQueueLoading,
    selectedTeamMembers,
    selectedTeamUserQueue,
    contactShare,
    distributeContactToQueue,
    totalContactsDistributed,
    totalContactsSelected,
    allContactsSelected,
    singleteamMember,
    showAddContactsModal,
    importContactMode,
    showContactUploadProgressmodal,
    uploadProgressPercentage,
    importCSVdialogVisible,
    handleFetchContact,
  };
};
